// Statuts vus par l'usager (H3-03) : ton d'affichage (texte coloré, jamais la couleur seule),
// action attendue, et chemin vertical à sept étapes de la fiche de suivi (écran S21).
import { STATUTS_USAGER, type StatutUsager } from '@cc/contrats';
import type { FicheDossierUsager, ResumeDossierUsager } from './types';

export type TonStatut = 'neutre' | 'info' | 'attention' | 'ok';

export const TON_STATUT: Record<StatutUsager, TonStatut> = {
  brouillon: 'neutre',
  enregistre: 'info',
  en_verification: 'info',
  incomplet: 'attention',
  en_instruction: 'info',
  en_attente_decision: 'info',
  approuve: 'ok',
  refuse: 'attention',
  termine: 'ok',
  classe_sans_suite: 'attention',
  retire: 'neutre',
};

export const STATUTS_TERMINES: StatutUsager[] = ['termine', 'classe_sans_suite', 'retire', 'refuse'];

export function libelleStatut(code: string): string {
  return STATUTS_USAGER[code as StatutUsager] ?? code;
}

export function tonStatut(code: string): TonStatut {
  return TON_STATUT[code as StatutUsager] ?? 'neutre';
}

/** Action attendue de l'usager, mise en évidence dans les listes et sur l'accueil. */
export function actionAttendue(d: Pick<ResumeDossierUsager, 'statut'>): string | null {
  if (d.statut.code === 'brouillon') return 'À terminer et déposer';
  if (d.statut.code === 'incomplet') return 'Complément à fournir';
  return null;
}

export function enCours(d: Pick<ResumeDossierUsager, 'statut'>): boolean {
  return !STATUTS_TERMINES.includes(d.statut.code as StatutUsager);
}

export interface EtapeChemin {
  rang: number;
  titre: string;
  detail: string;
  etat: 'faite' | 'courante' | 'a_venir';
}

const RANG: Record<StatutUsager, number> = {
  brouillon: 0,
  enregistre: 1,
  en_verification: 2,
  incomplet: 3,
  en_instruction: 4,
  en_attente_decision: 5,
  approuve: 6,
  refuse: 6,
  termine: 7,
  classe_sans_suite: 7,
  retire: 7,
};

export function rangStatut(code: string): number {
  return RANG[code as StatutUsager] ?? 0;
}

const dateCourte = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

/**
 * Les sept étapes du chemin : enregistrée, en vérification, incomplète puis complétée, en
 * instruction, en attente de décision, approuvée ou refusée, terminée. Les dates viennent de
 * l'historique des statuts usager (jamais des événements internes).
 */
export function cheminDossier(d: Pick<FicheDossierUsager, 'statut' | 'historique' | 'resultat' | 'echeance_reglementaire' | 'complement' | 'documents_delivres'>): EtapeChemin[] {
  const courant = rangStatut(d.statut.code);
  const dateDe = (...codes: string[]) => {
    const ev = d.historique.find((h) => codes.includes(h.statut));
    return ev ? dateCourte(ev.date) : null;
  };
  const complete = d.historique.some((h) => h.statut === 'incomplet') && courant > 3;
  const decision = d.resultat === 'refuse' ? 'Refusée' : d.resultat === 'approuve_sous_conditions' ? 'Approuvée sous conditions' : d.resultat ? 'Approuvée' : 'Approuvée ou refusée';
  const terminal = d.statut.code === 'classe_sans_suite' ? 'Classée sans suite' : d.statut.code === 'retire' ? 'Retirée' : 'Terminée';
  const etapes: { titre: string; detail: string }[] = [
    { titre: 'Enregistrée', detail: dateDe('enregistre') ? `${dateDe('enregistre')}, numéro attribué` : 'Numéro attribué au dépôt' },
    { titre: 'En vérification', detail: dateDe('en_verification') ?? 'Le service vérifie les pièces' },
    {
      titre: complete ? 'Incomplète, puis complétée' : courant === 3 ? 'Incomplète' : 'Pièces vérifiées',
      detail: courant === 3 ? (d.complement?.motif ? `Complément demandé : ${d.complement.motif}` : 'Un complément vous est demandé') : complete ? `Complément fourni${dateDe('incomplet') ? `, demandé le ${dateDe('incomplet')}` : ''}` : 'Aucun complément demandé',
    },
    { titre: 'En instruction', detail: dateDe('en_instruction') ?? 'Examen par le service compétent' },
    {
      titre: 'En attente de décision',
      detail: dateDe('en_attente_decision') ?? (d.echeance_reglementaire ? `Délai cible : ${dateCourte(d.echeance_reglementaire)}` : 'Décision de l\'autorité'),
    },
    { titre: decision, detail: dateDe('approuve', 'refuse') ?? 'Le motif et les conditions vous seront expliqués' },
    { titre: terminal, detail: d.documents_delivres.length > 0 ? `${d.documents_delivres.length} document${d.documents_delivres.length > 1 ? 's' : ''} délivré${d.documents_delivres.length > 1 ? 's' : ''}` : dateDe('termine', 'classe_sans_suite', 'retire') ?? 'Documents disponibles dans votre espace' },
  ];
  return etapes.map((e, i) => ({ rang: i + 1, ...e, etat: i + 1 < courant ? 'faite' : i + 1 === courant ? 'courante' : 'a_venir' }));
}

/** Texte de l'encart d'état : ce qui se passe et si une action est attendue. */
export function texteEtat(d: Pick<FicheDossierUsager, 'statut' | 'actions_possibles' | 'complement'>): { action: boolean; texte: string } {
  if (d.statut.code === 'brouillon') return { action: true, texte: 'Votre demande n\'est pas encore déposée : terminez le formulaire, joignez les pièces puis déposez-la.' };
  if (d.statut.code === 'incomplet') return { action: true, texte: d.statut.texte ?? 'Le service vous demande un complément. Sans réponse, la demande sera classée sans suite.' };
  if (d.statut.code === 'approuve') return { action: false, texte: d.statut.texte ?? 'Votre demande est approuvée. Le document délivré est disponible ci-dessous.' };
  if (d.statut.code === 'refuse') return { action: false, texte: d.statut.texte ?? 'Votre demande est refusée. Le motif figure ci-dessous.' };
  if (STATUTS_TERMINES.includes(d.statut.code as StatutUsager)) return { action: false, texte: d.statut.texte ?? 'Cette demande est terminée.' };
  return { action: false, texte: d.statut.texte ?? 'Le service traite votre demande. Vous n\'avez rien à faire : vous serez prévenu ici et par le canal choisi.' };
}
